// The support assistant's answers: the light Markdown its specification is written in (paragraphs, "-" lists,
// "1." steps, **bold** for what the user sees, *italic*, `code`), drawn as elements (never as HTML, so nothing in
// an answer can run). Anything else shows as written. While an answer streams in, a marker not yet closed shows
// as it is until its other half arrives.
import type { ReactNode } from 'react';
import { parseBlocks } from './markdownBlocks';
import styles from './SupportPanel.module.css';

// **bold**, `code`, then *italic* (a single star not followed by a space, so "5 * 3" stays as it is).
const INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\s][^*\n]*\*)/g;

function inline(text: string): ReactNode[] {
  // With a capturing group, split puts the markup at the odd places.
  return text.split(INLINE).map((part, i) => {
    if (i % 2 === 0) return part;
    if (part.startsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`')) return <code key={i}>{part.slice(1, -1)}</code>;
    return <em key={i}>{part.slice(1, -1)}</em>;
  });
}

export function Markdown({ text }: { text: string }) {
  return (
    <div className={styles.markdown}>
      {parseBlocks(text).map((block, b) => {
        if (block.kind === 'h')
          return (
            <p key={b}>
              <strong>{inline(block.lines[0]!)}</strong>
            </p>
          );
        if (block.kind === 'p') {
          return (
            <p key={b}>
              {block.lines.map((line, l) => (
                <span key={l}>
                  {l > 0 && <br />}
                  {inline(line)}
                </span>
              ))}
            </p>
          );
        }
        const items = block.lines.map((line, l) => <li key={l}>{inline(line)}</li>);
        return block.kind === 'ul' ? (
          <ul key={b}>{items}</ul>
        ) : (
          <ol key={b} start={block.start}>
            {items}
          </ol>
        );
      })}
    </div>
  );
}
